import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "reactstrap";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import ArticleIcon from "@mui/icons-material/Article";
import { RightDrawer, ModalHeader, ModalBody, ModalFooter } from "./RightDrawer";
import {
  downloadDocumentFile,
  getDocumentFileBlob,
} from "../../services/Documentservice";
import {
  canInlinePreview,
  isImageFile,
  isOfficeFile,
  isPdfFile,
  isTextFile,
  openBlobInNewTab,
} from "../../utils/attachmentUtils";
import { toastAlert } from "../../helpers/toastAlert";

export { isImageFile, isPdfFile } from "../../utils/attachmentUtils";

export function fileKindIcon(doc) {
  if (isPdfFile(doc)) {
    return <PictureAsPdfIcon sx={{ color: "#c62828", fontSize: 28 }} />;
  }
  if (isImageFile(doc)) {
    return <ImageIcon sx={{ color: "#1a3366", fontSize: 28 }} />;
  }
  if (isOfficeFile(doc)) {
    const name = String(doc?.fileName || "").toLowerCase();
    if (/\.xlsx?$/.test(name)) {
      return <TableChartIcon sx={{ color: "#2e7d32", fontSize: 28 }} />;
    }
    return <ArticleIcon sx={{ color: "#1565c0", fontSize: 28 }} />;
  }
  return <DescriptionIcon sx={{ color: "#2b4d8f", fontSize: 28 }} />;
}

export default function FilePreview({ document: doc }) {
  const token = useSelector((state) => state.authReducer?.accessToken);
  const [url, setUrl] = useState("");
  const [mime, setMime] = useState(doc?.fileType || "");
  const [textContent, setTextContent] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let objectUrl = "";
    let cancelled = false;
    const load = async () => {
      if (!doc?._id || !token) return;
      setLoading(true);
      setError("");
      setTextContent("");
      try {
        const result = await getDocumentFileBlob(doc._id, token);
        objectUrl = result.url;
        if (cancelled) {
          window.URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(result.url);
        setMime(doc.fileType || result.mime || "");
        if (isTextFile({ ...doc, fileType: result.mime })) {
          const text = await result.blob.text();
          if (!cancelled) setTextContent(text);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Could not load this file.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [doc?._id, doc?.fileType, token]);

  if (!doc) return null;
  if (error) return <p className="error-message">{error}</p>;
  if (loading || !url) {
    return <p style={{ color: "#64748b" }}>Loading preview…</p>;
  }

  const meta = { ...doc, fileType: mime || doc.fileType };

  if (isImageFile(meta)) {
    return (
      <img
        src={url}
        alt={doc.fileName}
        style={{
          maxWidth: "100%",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
        }}
      />
    );
  }

  if (isPdfFile(meta)) {
    return (
      <iframe
        title={doc.fileName}
        src={url}
        style={{
          width: "100%",
          height: "70vh",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
        }}
      />
    );
  }

  if (isTextFile(meta) && textContent !== undefined) {
    return (
      <pre
        style={{
          maxHeight: "70vh",
          overflow: "auto",
          padding: 16,
          background: "#0f172a",
          color: "#e2e8f0",
          borderRadius: 8,
          fontSize: 13,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {textContent || "(empty file)"}
      </pre>
    );
  }

  return (
    <div style={{ textAlign: "center", padding: "24px 8px" }}>
      <div style={{ marginBottom: 12 }}>{fileKindIcon(meta)}</div>
      <p style={{ color: "#334155", marginBottom: 8, fontWeight: 600 }}>
        {doc.fileName}
      </p>
      <p style={{ color: "#64748b", marginBottom: 16 }}>
        {isOfficeFile(meta)
          ? "Office files open best in Word/Excel. Download or open in a new tab."
          : "In-app preview is not available for this type. Download to open it."}
      </p>
    </div>
  );
}

export function FilePreviewDrawer({ document: doc, onClose }) {
  const token = useSelector((state) => state.authReducer?.accessToken);
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    if (!doc?._id || !token) {
      toastAlert("Unable to download — missing auth or file id", "error");
      return;
    }
    try {
      setBusy(true);
      await downloadDocumentFile(doc._id, token, doc.fileName || "document");
      toastAlert("Download started", "success");
    } catch (err) {
      toastAlert(err?.message || "Download failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleOpenExternal = async () => {
    if (!doc?._id || !token) return;
    try {
      setBusy(true);
      const { url } = await getDocumentFileBlob(doc._id, token);
      openBlobInNewTab(url);
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toastAlert(err?.message || "Could not open file", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <RightDrawer isOpen={Boolean(doc)} onClose={onClose} size="lg">
      <ModalHeader toggle={onClose} kicker="Document">
        {doc?.fileName || "Preview"}
      </ModalHeader>
      <ModalBody>
        <FilePreview document={doc} />
      </ModalBody>
      <ModalFooter>
        <Button className="btn btn-border" onClick={onClose} disabled={busy}>
          Close
        </Button>
        {doc && canInlinePreview(doc) && (
          <Button
            className="btn btn-border"
            onClick={handleOpenExternal}
            disabled={busy}
          >
            Open in tab
          </Button>
        )}
        <Button
          className="btn btn-colored"
          onClick={handleDownload}
          disabled={busy || !doc}
        >
          {busy ? "Please wait…" : "Download"}
        </Button>
      </ModalFooter>
    </RightDrawer>
  );
}
